import { motion } from "framer-motion";
import enSampradayData from "~/data/about-us/sampraday.json";
import gujSampradayData from "~/data/about-us/sampraday_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9, rotateX: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

// Hero Section with Title
const HeroSection = ({ sampradayData }: { sampradayData: typeof enSampradayData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-5xl">
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{sampradayData.title}</h1>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto" />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section with Timeline Style
const IntroductionSection = ({ sampradayData }: { sampradayData: typeof enSampradayData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div variants={titleVariants}>
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {sampradayData.origin.title}
          </h2>
          {sampradayData.origin.description.length > 0 &&
            sampradayData.origin.description.map((desc, index) => (
              <p
                key={index}
                className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify mt-6", normalInter)}
              >
                {desc}
              </p>
            ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Image Showcase Section
const ImageShowcaseSection = ({ sampradayData }: { sampradayData: typeof enSampradayData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/20 hover:border-primaryColor/50 transition-all duration-300 group">
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <motion.img
            transition={{ duration: 0.5 }}
            src={sampradayData.mainImage.src}
            alt={sampradayData.mainImage.alt}
            className="w-full aspect-auto object-contain"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-8">
            <p className={cn("text-white text-lg md:text-xl font-semibold drop-shadow-lg capitalize", normalInter)}>{sampradayData.mainImage.alt}</p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Philosophy Section with Cards
const PhilosophySection = ({ sampradayData }: { sampradayData: typeof enSampradayData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, rotateX: 10 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateX: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 md:mb-20 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div variants={titleVariants} className="mb-4">
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor inline-block border-b-4 border-primaryColor pb-2", medium)}>
            {sampradayData.philosophyOfVishishtadvaita.title}
          </h2>
          {sampradayData.philosophyOfVishishtadvaita.description.length > 0 &&
            sampradayData.philosophyOfVishishtadvaita.description.map((desc, index) => (
              <p
                key={index}
                className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify mt-6", normalInter)}
              >
                {desc}
              </p>
            ))}
        </motion.div>

        {/* Philosophy Elements Cards */}
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sampradayData.philosophyOfVishishtadvaita.philosophyElements.map((item, index) => (
            <motion.div
              key={item.key}
              variants={cardVariants}
              className="p-4 rounded-xl border-2 border-primaryColor/30 shadow-lg hover:border-primaryColor transition-all duration-300 group"
            >
              <div>
                {/* Title */}
                <h3 className={cn("text-xl font-bold text-primaryColor mb-3 group-hover:text-textColor transition-colors duration-300", medium)}>
                  {item.key}
                </h3>

                {/* Description */}
                <p className={cn("text-textSecondaryColor text-sm md:text-base paragraph-text-size 2xl:text-lg leading-relaxed", normalInter)}>
                  {item.value}
                </p>
              </div>

              {/* Decorative Bottom Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-4 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Conclusion Section
const ConclusionSection = ({ sampradayData }: { sampradayData: typeof enSampradayData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div className="rounded-2xl p-8 md:p-12 border-2 border-primaryColor/40 shadow-xl" style={BACKGROUND_IMAGE_STYLE}>
          <motion.div
            initial={{ scale: 0 }}
            animate={isVisible ? { scale: 1 } : { scale: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-16 h-16 bg-primaryColor rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>

          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6 text-center", medium)}>{sampradayData.conclusion.title}</h2>

          <p className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-center", normalInter)}>
            {sampradayData.conclusion.description}
          </p>

          {/* Decorative elements */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0 }}
                animate={isVisible ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.3 }}
                className="w-2 h-2 bg-primaryColor rounded-full"
              />
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

function Sampraday() {
  const lang = useLanguageParams();
  const sampradayData = lang === "gu" ? gujSampradayData : enSampradayData;

  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      <div className="w-full">
        <HeroSection sampradayData={sampradayData} />
        <IntroductionSection sampradayData={sampradayData} />
        <ImageShowcaseSection sampradayData={sampradayData} />
        <PhilosophySection sampradayData={sampradayData} />
        <ConclusionSection sampradayData={sampradayData} />
      </div>
    </div>
  );
}

export default Sampraday;
